// btw-front/src/components/admin/dashboard/PartnersTab.jsx
// [파트너] 탭 — 파트너사 목록(딜 등록 허용/제한) + 회사 행을 펼치면 소속 계정 목록.
// 종전 [계정] 탭을 여기로 흡수했다. 회사가 관리 단위(딜 등록 제한·계정 보유)라 한 화면에서 처리한다.
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { message, Modal, Switch } from "antd";
import { Badge } from "../../ui/Badge";
import { Input } from "../../ui/Input";
import { EmptyState } from "../../ui/EmptyState";
import * as adminPartners from "../../../api/adminPartners";
import * as adminPartnerUsers from "../../../api/adminPartnerUsers";
import { buildPartnerRows, groupUsersByPartner } from "../../../lib/adminPartnerSearch";

// 제한(canRegisterDeal=false)은 신규 딜 등록만 막는다 — 기존 딜 조회·수정·삭제는 그대로.
// 제한된 파트너가 등록을 시도하면 안내 팝업이 뜨고, 서버도 403 으로 차단한다.
// 상태 라벨 — partner.status 3값을 그대로 구분해 표시(정지와 비활성은 다른 상태)
const STATUS_LABEL_KEYS = {
  active: "admin.partners.statusActive",
  inactive: "admin.partners.statusInactive",
  suspended: "admin.partners.statusSuspended",
  deleted: "admin.partners.statusDeleted",
};

export default function PartnersTab() {
  const { t } = useTranslation();
  const [partners, setPartners] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [togglingId, setTogglingId] = useState(null); // 권한 변경 요청 중인 파트너 id
  const [deletingId, setDeletingId] = useState(null); // 삭제 요청 중인 파트너 id
  const [expanded, setExpanded] = useState({}); // { [partnerId]: true }
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // 이메일 변경 대상 계정 row
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // 각각 따로 실패 처리 — 계정 목록이 실패해도 회사 목록(딜 등록 제한)은 계속 쓸 수 있어야 한다
    Promise.all([adminPartners.list().catch(() => []), adminPartnerUsers.list().catch(() => [])])
      .then(([partnerRows, userRows]) => {
        if (cancelled) return;
        setPartners(partnerRows);
        setUsers(userRows);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const usersByPartner = useMemo(() => groupUsersByPartner(users), [users]);

  const q = search.trim();
  const visible = useMemo(
    () => buildPartnerRows(partners, usersByPartner, q),
    [partners, usersByPartner, q],
  );

  // 검색어가 바뀌면 수동 펼침/접힘을 초기화 — 새 검색 결과는 기본 상태에서 보여 준다
  useEffect(() => {
    setExpanded({});
  }, [q]);

  // 계정 검색으로 걸린 회사(hit)는 기본 펼침. 단 관리자가 직접 접으면 그 조작을 우선한다.
  const isExpanded = (row) => {
    const manual = expanded[row.partner.id];
    return manual === undefined ? row.hit : manual;
  };
  const toggleExpand = (row) =>
    setExpanded((prev) => ({ ...prev, [row.partner.id]: !isExpanded(row) }));

  // 제한(차단) 방향은 오클릭 시 파트너가 바로 막히므로 확인 단계를 둔다. 허용은 즉시 적용.
  function handleToggle(row, next) {
    if (next) return applyDealPermission(row, next);
    Modal.confirm({
      title: t("admin.partners.confirmBlock.title"),
      content: t("admin.partners.confirmBlock.body", { company: row.companyName }),
      okText: t("admin.partners.confirmBlock.ok"),
      cancelText: t("admin.partners.confirmBlock.cancel"),
      okButtonProps: { danger: true },
      onOk: () => applyDealPermission(row, next),
    });
  }

  function applyDealPermission(row, next) {
    setTogglingId(row.id);
    adminPartners
      .updateDealPermission(row.id, next)
      // 낙관적 업데이트 X — 변경자·시각까지 서버 값으로 보여 주기 위해 목록을 다시 읽는다
      .then((res) => {
        const value = res && typeof res.canRegisterDeal === "boolean" ? res.canRegisterDeal : next;
        setReloadKey((k) => k + 1);
        message.success(
          value
            ? t("admin.partners.toasts.allowed", { company: row.companyName })
            : t("admin.partners.toasts.blocked", { company: row.companyName }),
        );
      })
      .catch((e) => {
        const msg =
          (e && e.payload && e.payload.error && e.payload.error.message) ||
          t("admin.partners.toasts.saveFailed");
        message.error(msg);
      })
      .finally(() => setTogglingId(null));
  }

  // 삭제는 되돌릴 수 없고, 그 회사의 모든 계정 로그인이 막히며 등록한 딜도 함께 삭제된다
  // — 딜 건수까지 보여 주고 확인 창을 반드시 거친다
  function handleDelete(row) {
    // 서버는 삭제되지 않은 딜 **전체**를 지운다(심사 중 포함) — dealCount 는 승인건만이라
    // 그것만 보여 주면 실제 삭제 건수보다 적게 안내된다. 두 값을 합해 보여준다.
    const affectedDeals = (row.dealCount || 0) + (row.pendingDealCount || 0);
    Modal.confirm({
      title: t("admin.partners.confirmDelete.title"),
      content: (
        <div>
          <p>{t("admin.partners.confirmDelete.body", { company: row.companyName })}</p>
          {/* 한 회사에 계정이 여러 개일 수 있다 — 몇 개가 함께 삭제되는지 알린다(서버 user_count) */}
          {(row.userCount || 0) > 0 && (
            <p className="mt-2 text-sm font-medium text-rose-700">
              {t("admin.partners.confirmDelete.accounts", { count: row.userCount })}
            </p>
          )}
          {affectedDeals > 0 && (
            <p className="mt-2">
              {t("admin.partners.confirmDelete.deals", { count: affectedDeals })}
            </p>
          )}
          <p className="mt-2 text-xs text-slate-500">{t("admin.partners.confirmDelete.hint")}</p>
          {/* 삭제는 비가역이지만 재가입은 막지 않는다 — 사업자번호의 유니크 범위가 '삭제되지 않은 행'
              으로 좁혀져 있어(active_business_number) 점유가 풀린다. 계정 이메일은 원래 중복 허용이다.
              운영자가 "지우면 다시 못 들어온다" 로 오해하지 않도록 함께 알린다. */}
          <p className="mt-1 text-xs text-slate-500">{t("admin.partners.confirmDelete.rejoin")}</p>
        </div>
      ),
      okText: t("admin.partners.confirmDelete.ok"),
      cancelText: t("admin.partners.confirmDelete.cancel"),
      okButtonProps: { danger: true },
      onOk: () => applyDelete(row),
    });
  }

  function applyDelete(row) {
    setDeletingId(row.id);
    return adminPartners
      .remove(row.id)
      .then((res) => {
        setReloadKey((k) => k + 1);
        // 함께 삭제된 딜이 있으면 어디서 볼 수 있는지까지 알린다([삭제된 딜] 탭)
        const count = Number((res && res.deletedDealCount) || 0);
        message.success(
          count > 0
            ? t("admin.partners.toasts.deletedWithDeals", { company: row.companyName, count })
            : t("admin.partners.toasts.deleted", { company: row.companyName }),
        );
      })
      .catch((e) => {
        const msg =
          (e && e.payload && e.payload.error && e.payload.error.message) ||
          t("admin.partners.toasts.deleteFailed");
        message.error(msg);
      })
      .finally(() => setDeletingId(null));
  }

  function openEdit(user) {
    setEditing(user);
    setNewEmail(user.email || "");
  }

  async function handleSaveEmail() {
    if (!editing || saving) return;
    const email = newEmail.trim().toLowerCase();
    if (!email || email === editing.email) {
      setEditing(null);
      return;
    }
    setSaving(true);
    try {
      await adminPartnerUsers.updateEmail(editing.id, email);
      message.success(t("admin.accounts.toasts.saved"));
      setEditing(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      const code = e && e.payload && e.payload.error && e.payload.error.code;
      // 이메일은 중복을 허용하므로(로그인 ID 만 유일) 중복 오류는 없다
      message.error(
        code === "INVALID_EMAIL"
          ? t("admin.accounts.toasts.invalidEmail")
          : (e && e.payload && e.payload.error && e.payload.error.message) ||
              t("admin.accounts.toasts.saveFailed"),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-slate-800">{t("admin.partners.title")}</p>
            <p className="mt-0.5 text-xs text-slate-500">{t("admin.partners.desc")}</p>
          </div>
          <Input
            className="w-full sm:w-72"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("admin.partners.searchPlaceholder")}
          />
        </div>

        {loading ? (
          <p className="py-10 text-center text-sm text-slate-400">{t("common.loading")}</p>
        ) : visible.length === 0 ? (
          <EmptyState title={q ? t("admin.partners.searchEmpty") : t("admin.empty")} />
        ) : (
          // 좁은 화면에서는 열을 쥐어짜는 대신 표 전체를 가로로 넘긴다.
          // min-w 가 없으면 삭제 버튼 같은 짧은 라벨이 세로로 쪼개진다.
          // tabIndex — 스크롤 영역은 포커스를 받아야 키보드만으로도 가로 이동이 된다.
          <div
            className="overflow-x-auto"
            tabIndex={0}
            role="region"
            aria-label={t("admin.partners.title")}
          >
            <table className="w-full min-w-[896px] text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr className="whitespace-nowrap">
                  <th className="px-4 py-2.5 text-left font-medium">
                    {t("admin.partners.company")}
                  </th>
                  <th className="px-4 py-2.5 text-left font-medium">
                    {t("admin.partners.businessNumber")}
                  </th>
                  <th className="px-4 py-2.5 text-left font-medium">
                    {t("admin.partners.status")}
                  </th>
                  <th className="px-4 py-2.5 text-right font-medium">
                    {t("admin.partners.deals")}
                  </th>
                  <th className="px-4 py-2.5 text-left font-medium">
                    {t("admin.partners.dealPermission")}
                  </th>
                  {/* 삭제 열은 맨 오른쪽 — 버튼이 한 줄로 들어갈 만큼만 잡아 앞 열을 밀지 않는다 */}
                  <th className="w-28 px-4 py-2.5 text-right font-medium">
                    {t("admin.partners.deleteColumn")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visible.map((row) => {
                  const p = row.partner;
                  const open = isExpanded(row);
                  return (
                    <React.Fragment key={p.id}>
                      <tr
                        className={`transition hover:bg-slate-50 ${p.isDeleted ? "bg-slate-50/70" : ""}`}
                      >
                        <td className="px-4 py-2.5">
                          <button
                            type="button"
                            onClick={() => toggleExpand(row)}
                            className={`flex items-center gap-1.5 text-left font-medium ${
                              p.isDeleted ? "text-slate-400" : "text-slate-900"
                            }`}
                            aria-expanded={open}
                          >
                            <span className="w-3 text-xs text-slate-400">{open ? "▾" : "▸"}</span>
                            {p.companyName}
                          </button>
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">
                          {p.businessNumber || "-"}
                        </td>
                        <td className="px-4 py-2.5">
                          <Badge tone={p.status === "active" ? "approved" : "rejected"}>
                            {t(STATUS_LABEL_KEYS[p.status] || "admin.partners.statusInactive")}
                          </Badge>
                          {p.isDeleted && p.deletedAt && (
                            <p className="mt-1 text-xs text-slate-400">
                              {t("admin.partners.deletedMeta", {
                                date: String(p.deletedAt).slice(0, 10),
                                admin: p.deletedByName || "-",
                              })}
                            </p>
                          )}
                        </td>
                        {/* 삭제된 회사는 활성 딜이 0 이 된다 — 어디로 갔는지 보이도록 삭제 건수를 덧붙인다 */}
                        <td className="whitespace-nowrap px-4 py-2.5 text-right text-slate-600">
                          {p.dealCount}
                          {p.deletedDealCount > 0 && (
                            <p className="mt-1 text-xs text-slate-400">
                              {t("admin.partners.deletedDeals", { count: p.deletedDealCount })}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            <Switch
                              size="small"
                              checked={p.canRegisterDeal}
                              loading={togglingId === p.id}
                              disabled={p.isDeleted}
                              onChange={(next) => handleToggle(p, next)}
                            />
                            <span
                              className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                                p.canRegisterDeal
                                  ? "bg-emerald-50 text-emerald-700"
                                  : "bg-rose-50 text-rose-700"
                              }`}
                            >
                              {p.canRegisterDeal
                                ? t("admin.partners.permitAllowed")
                                : t("admin.partners.permitBlocked")}
                            </span>
                          </div>
                          {p.dealPermissionChangedAt && (
                            <p className="mt-1 text-xs text-slate-400">
                              {t("admin.partners.lastChanged", {
                                date: String(p.dealPermissionChangedAt).slice(0, 10),
                                admin: p.dealPermissionChangedByName || "-",
                              })}
                            </p>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right">
                          {p.isDeleted ? (
                            <span className="text-xs text-slate-300">-</span>
                          ) : (
                            // inline-flex + nowrap — '삭제 중…'(en: Deleting…)으로 바뀌어도 한 줄을 유지한다
                            <button
                              type="button"
                              onClick={() => handleDelete(p)}
                              disabled={deletingId === p.id}
                              className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {deletingId === p.id
                                ? t("admin.partners.deleting")
                                : t("admin.partners.delete")}
                            </button>
                          )}
                        </td>
                      </tr>
                      {open && (
                        <tr className="bg-slate-50/60">
                          <td colSpan={6} className="px-4 py-3">
                            <AccountList
                              users={row.users}
                              // 삭제된 회사는 계정 정보도 건드리지 않는다(삭제 버튼·딜 토글과 동일 기준)
                              onChangeEmail={p.isDeleted ? null : openEdit}
                              emptyText={t("admin.partners.noAccounts")}
                              t={t}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Modal
        title={t("admin.accounts.modalTitle")}
        open={!!editing}
        onCancel={() => setEditing(null)}
        footer={[
          <button
            key="cancel"
            onClick={() => setEditing(null)}
            disabled={saving}
            className="btw-btn-ghost mr-2 disabled:opacity-60"
          >
            {t("admin.accounts.cancel")}
          </button>,
          <button
            key="save"
            onClick={handleSaveEmail}
            disabled={saving}
            className="btw-btn-accent disabled:opacity-60"
          >
            {t("admin.accounts.save")}
          </button>,
        ]}
      >
        {editing && (
          <div className="grid gap-3 text-sm">
            <p className="text-slate-600">
              {editing.companyName} · {editing.loginId} ({editing.name})
            </p>
            <Input
              label={t("admin.accounts.newEmail")}
              type="email"
              required
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              hint={t("admin.accounts.newEmailHint")}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}

// 회사 행을 펼쳤을 때 보이는 소속 계정 목록(= 가입 시 등록한 대표 담당자).
// 상태는 회사 행에서 이미 보여 주므로 여기서는 생략한다.
// onChangeEmail 이 없으면(삭제된 회사) 변경 버튼을 숨긴다
function AccountList({ users, onChangeEmail, emptyText, t }) {
  if (!users.length) {
    return <p className="py-2 text-center text-xs text-slate-400">{emptyText}</p>;
  }
  return (
    <table className="w-full text-xs">
      <thead className="text-[11px] uppercase tracking-wider text-slate-400">
        <tr>
          <th className="px-2 py-1.5 text-left font-medium">{t("admin.accounts.loginId")}</th>
          <th className="px-2 py-1.5 text-left font-medium">{t("admin.accounts.name")}</th>
          <th className="px-2 py-1.5 text-left font-medium">{t("admin.accounts.email")}</th>
          <th className="px-2 py-1.5 text-left font-medium">{t("admin.accounts.phone")}</th>
          <th className="px-2 py-1.5 text-left font-medium">{t("admin.accounts.lastLogin")}</th>
          <th className="px-2 py-1.5 text-right font-medium"></th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-200/70">
        {users.map((u) => (
          <tr key={u.id}>
            <td className="px-2 py-1.5 text-slate-700">{u.loginId}</td>
            <td className="px-2 py-1.5 text-slate-700">{u.name}</td>
            <td className="px-2 py-1.5 text-slate-600">{u.email}</td>
            <td className="px-2 py-1.5 text-slate-600">{u.phone || "-"}</td>
            <td className="px-2 py-1.5 text-slate-500">
              {u.lastLoginAt ? String(u.lastLoginAt).slice(0, 10) : "-"}
            </td>
            <td className="px-2 py-1.5 text-right">
              {onChangeEmail && (
                <button
                  type="button"
                  onClick={() => onChangeEmail(u)}
                  className="text-xs text-accent underline-offset-2 hover:underline"
                >
                  {t("admin.accounts.changeEmail")}
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
